import { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { BfiDataGrid, Column, MasterDetail, Summary, TotalItem } from '../../common/SharedComponents';
import { formatCurrency, formatPercent } from '../../common/SharedFunctions';
import { drawFilterTypes } from '../../common/SharedConstants';
import { GridSettingType } from '@apply/constants';
import { LoanStatusType, StructureTypeOptions } from '../common/LoanConstants';
import { LoanApi } from '@apply/api';
import { applicationInsights } from '../../ApplicationInsights';
import {
    onActionCellRender,
    onBorrowerCellRender,
    onStateCellRender,
    onCityCellRender,
    OnOpenLoanDetail,
    onRenderPortfolioGrid,
    onRowPrepared,
} from '../common/LoanFunctions';
import { LoanCommitOrApprovalColumn } from '../../common/SharedComponents';
import { LoanRepaidIndicatorColumn } from '../common/components/LoanRepaidIndicatorColumn';
import { LoanApprovedOrNoteDateColumn } from '../common/components/LoanApprovedOrNoteDateColumn';
import { onStructureTypeCellRender } from '../common/functions/onStructureTypeCellRender';
import { onLoanTermCellRender } from '../common/functions/onLoanTermCellRender';

const SubNoteOutstandingTable = ({ gridStore }) => {
    const searchState = useSelector((state) => state.searchState);
    const loanApi = new LoanApi.LoanApi(applicationInsights);
    const location = useLocation();

    const [dataStore, set_dataStore] = useState(gridStore?.store);

    const [drawFilterType, set_drawFilterType] = useState();
    const drawFilterTypeRef = useRef(drawFilterType);

    const dataFunction = (criteria) => {
        criteria.statusId = LoanStatusType.Outstanding;
        if (drawFilterTypeRef.current) criteria.drawFilterType = drawFilterTypeRef.current;

        return loanApi.getGrid_SubNotes(criteria);
    };

    useEffect(() => {
        if (gridStore) {
            gridStore.setOnLoad('id', dataFunction, searchState?.searchText?.loan);
            set_dataStore(gridStore.store);
        }
    }, [gridStore]);

    useEffect(() => {
        drawFilterTypeRef.current = drawFilterType;
    }, [drawFilterType]);

    const onPresoldRender = (e) => {
        return e.data.isPreSold ? 'Yes' : 'No';
    };

    const customizeTotalValue = useCallback((e) => {
        return e.value === 1 ? e.value + ' Line' : e.value + ' Lines ';
    });

    if (!gridStore) {
        return '';
    }

    const getFundedStatusText = (data) => {
        const {
            drawBudgetId,
            borrowerRequested,
            inspectionOrdered,
            inspectedOn,
            fundedOn,
            accommodationCount,
            outOfBalanceCount,
        } = data;
        if (!drawBudgetId) return 'NO BUDGET';
        if (borrowerRequested && !inspectionOrdered && !inspectedOn && !fundedOn) return '$ REQUEST';
        if (borrowerRequested && inspectionOrdered && !inspectedOn) return 'INSPECTING';
        if (borrowerRequested && inspectionOrdered && inspectedOn && !fundedOn) return 'NEED TO FUND';
        if (accommodationCount > 0) return 'Accommodation';
        if (outOfBalanceCount > 0) return 'Out-of-Balance';
        return null;
    };

    const fundedDollarRender = (e) => {
        const status = getFundedStatusText(e.data);
        if (status) return <p>{status}</p>;
        return <p>{formatCurrency(e.data.fundedTotal, 0)}</p>;
    };

    const fundedPercentRender = (e) => {
        if (getFundedStatusText(e.data)) return null;
        return <p>{formatPercent(e.data.budgetTotal ? (e.data.fundedTotal ?? 0) / e.data.budgetTotal : 0, 0)}</p>;
    };

    return (
        <>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <div style={{ display: 'flex', marginTop: 20, marginRight: 20 }}>
                    <p
                        style={{
                            marginRight: 10,
                            marginTop: 5,
                            fontSize: 17,
                            color: '#344077',
                        }}
                    >
                        Show:
                    </p>
                    <select
                        id='targetLoanTypeId'
                        value={drawFilterType}
                        onChange={(event) => set_drawFilterType(event.target.value)}
                    >
                        <option value=''>All</option>
                        {drawFilterTypes.map((d) => {
                            return (
                                <option
                                    key={d.id}
                                    value={d.type}
                                >
                                    {d.name}
                                </option>
                            );
                        })}
                    </select>
                </div>
            </div>
            <BfiDataGrid
                id={`snot_table`}
                gridType={GridSettingType.SubNotesTable_Outstanding}
                dataStore={dataStore}
                applicationInsights={applicationInsights}
                onCellDblClick={(e) => OnOpenLoanDetail(e, location)}
                onRowPrepared={onRowPrepared}
            >
                <Column
                    key='borrowerEntity'
                    dataField='borrowerEntity'
                    caption='Entity'
                    cellRender={onBorrowerCellRender}
                    name='borrowerEntity'
                    minWidth={120}
                />
                <Column
                    key='propertyCity'
                    dataField='propertyCity'
                    caption='City'
                    cellRender={onCityCellRender}
                    width={90}
                />
                <Column
                    key='propertyState'
                    dataField='propertyState'
                    caption='State'
                    cellRender={onStateCellRender}
                    width={70}
                />
                <Column
                    key='bfiNumber'
                    dataField='bfiNumber'
                    caption='BFI #'
                    dataType='string'
                    width={80}
                />
                <Column
                    key='loanNumber'
                    dataField='loanNumber'
                    caption='Loan #'
                    dataType='string'
                    width={80}
                />
                {LoanApprovedOrNoteDateColumn({ statusId: LoanStatusType.Outstanding })}
                <Column
                    key='monthsSinceClosing'
                    dataField='monthsSinceClosing'
                    caption='Mnths'
                    width={60}
                    alignment={'left'}
                />
                <Column
                    key='origMaturityDate'
                    dataField='origMaturityDate'
                    caption='Maturity'
                    dataType='date'
                    width={90}
                />
                {LoanCommitOrApprovalColumn({ statusId: LoanStatusType.Outstanding })}
                <Column
                    key='term'
                    dataField='initialTermMonths'
                    caption='Term'
                    dataType='string'
                    width={60}
                    alignment={'left'}
                    cellRender={onLoanTermCellRender}
                />
                <Column
                    key='fundedDollar'
                    dataField='fundedTotal'
                    caption='Funded'
                    dataType='number'
                    format='currency'
                    width={90}
                    alignment={'left'}
                    cellRender={fundedDollarRender}
                />
                <Column
                    key='fundedPercent'
                    caption='Funded'
                    dataType='number'
                    width={70}
                    alignment={'left'}
                    cellRender={fundedPercentRender}
                    allowSorting={false}
                    cssClass='no-sort-header'
                />
                <Column
                    key='interestRate'
                    dataField='interestRate'
                    caption='Rate'
                    dataType='number'
                    format={{ type: 'percent', precision: 2 }}
                    width={90}
                    alignment={'left'}
                />
                <Column
                    key='loanToValue'
                    dataField='loanToValue'
                    caption='LTV'
                    dataType='number'
                    format={{ type: 'percent', precision: 1 }}
                    width={80}
                    alignment={'left'}
                />
                <Column
                    key='structureTypeId'
                    dataField='structureTypeId'
                    caption='Structure Type'
                    width={80}
                    cellRender={onStructureTypeCellRender}
                    alignment={'left'}
                />
                <Column
                    key='isPreSold'
                    dataField='isPreSold'
                    caption='Pre-Sold'
                    width={60}
                    cellRender={onPresoldRender}
                    alignment={'left'}
                />
                <Column
                    key='pointOfContact'
                    dataField='pointOfContact'
                    caption='POC'
                    dataType='string'
                    width={110}
                />
                <Column
                    key='ownerName'
                    dataField='ownerName'
                    caption='User'
                    dataType='string'
                    width={110}
                />
                {LoanRepaidIndicatorColumn({ statusId: LoanStatusType.Outstanding })}
                <Column
                    alignment='center'
                    cellRender={onActionCellRender}
                    width={50}
                    minWidth={50}
                    allowReordering={false}
                    allowSorting={false}
                    allowResizing={false}
                    type='actions'
                    key='actions'
                    fixed={true}
                    fixedPosition={'right'}
                />
                <MasterDetail
                    enabled={true}
                    render={(e) => onRenderPortfolioGrid(e, LoanStatusType.Outstanding)}
                />
                <Summary>
                    <TotalItem
                        showInColumn='borrowerEntity'
                        customizeText={customizeTotalValue}
                    />
                    <TotalItem
                        column='commitment'
                        valueFormat='currency'
                    />
                </Summary>
            </BfiDataGrid>
        </>
    );
};

export default SubNoteOutstandingTable;
